import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Card, CardSection, Badge, Button } from '@trafiflow/shared-ui'
import { Search, X } from 'lucide-react'
import { MultiSelect } from '../components/shared/MultiSelect'
import { changelogApi, type ChangelogEntry } from '../lib/funnel-api'
import { getErrorMessage, type ApiError } from '../lib/api-error'
import { formatChanges } from '../lib/format-changes'
import { Pagination } from '../components/shared/Pagination'
import { inputClass } from '../styles/class-utils'

// Utilities
function getEntityTypeBadgeVariant(type: string) {
  switch (type) {
    case 'funnel':
      return 'default'
    case 'offer':
      return 'success'
    case 'step':
      return 'warning'
    default:
      return 'default'
  }
}

function getActionBadgeVariant(action: string) {
  switch (action) {
    case 'created':
      return 'success'
    case 'updated':
      return 'default'
    case 'deleted':
      return 'critical'
    case 'duplicated':
      return 'warning'
    default:
      return 'default'
  }
}

// Custom Hook
interface ChangelogFilters {
  entityTypeFilter: string[]
  actionFilter: string[]
  dateFromFilter: string
  dateToFilter: string
}

function useChangelog(filters: ChangelogFilters) {
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchChangelog = useCallback(async (params: Record<string, string>) => {
    try {
      setLoading(true)
      setError(null)
      const response = await changelogApi.list(params)
      setEntries(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error('Error fetching changelog:', (err as ApiError).message)
      setError(getErrorMessage(err as ApiError, 'Failed to load changelog.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const params: Record<string, string> = {}
      if (filters.entityTypeFilter.length > 0) params.entityType = filters.entityTypeFilter.join(',')
      if (filters.actionFilter.length > 0) params.action = filters.actionFilter.join(',')
      if (filters.dateFromFilter) params.from = filters.dateFromFilter
      if (filters.dateToFilter) params.to = `${filters.dateToFilter}T23:59:59Z`
      fetchChangelog(params)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [filters.entityTypeFilter, filters.actionFilter, filters.dateFromFilter, filters.dateToFilter, fetchChangelog])

  return { entries, loading, error, fetchChangelog }
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'funnel', label: 'Funnel' },
  { value: 'offer', label: 'Offer' },
  { value: 'step', label: 'Step' },
]

const ACTION_OPTIONS = [
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'duplicated', label: 'Duplicated' },
]

// Sub-components
interface FilterSectionProps {
  searchQuery: string
  entityTypeFilter: string[]
  actionFilter: string[]
  dateFromFilter: string
  dateToFilter: string
  hasActiveFilters: boolean
  onSearchChange: (value: string) => void
  onEntityTypeChange: (value: string[]) => void
  onActionChange: (value: string[]) => void
  onDateFromChange: (value: string) => void
  onDateToChange: (value: string) => void
  onClearFilters: () => void
}

function FilterSection(props: FilterSectionProps) {
  return (
    <Card>
      <CardSection className="p-4">
        <div className="space-y-3">
          {/* Row 1: Search (full width) */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            <input
              type="text"
              className={`${inputClass} pl-9`}
              placeholder="Search by entity ID or actor..."
              value={props.searchQuery}
              onChange={(e) => props.onSearchChange(e.target.value)}
            />
          </div>
          {/* Row 2: Filters */}
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <MultiSelect
              options={ENTITY_TYPE_OPTIONS}
              selected={props.entityTypeFilter}
              onChange={props.onEntityTypeChange}
              placeholder="All Entity Types"
            />
            <MultiSelect
              options={ACTION_OPTIONS}
              selected={props.actionFilter}
              onChange={props.onActionChange}
              placeholder="All Actions"
            />
            <input
              type="date"
              className={inputClass}
              value={props.dateFromFilter}
              onChange={(e) => props.onDateFromChange(e.target.value)}
              title="From date"
            />
            <input
              type="date"
              className={inputClass}
              value={props.dateToFilter}
              onChange={(e) => props.onDateToChange(e.target.value)}
              title="To date"
            />
          </div>
        </div>
        {props.hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <Button variant="secondary" onClick={props.onClearFilters} className="text-sm">
              <X className="w-4 h-4 mr-1" />Clear
            </Button>
          </div>
        )}
      </CardSection>
    </Card>
  )
}

// Main Component
const CHANGELOG_PAGE_SIZE = 10

export default function Changelog() {
  const [searchQuery, setSearchQuery] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState<string[]>([])
  const [actionFilter, setActionFilter] = useState<string[]>([])
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const { entries, loading, error, fetchChangelog } = useChangelog({
    entityTypeFilter,
    actionFilter,
    dateFromFilter,
    dateToFilter,
  })

  const clearFilters = () => {
    setEntityTypeFilter([])
    setActionFilter([])
    setDateFromFilter('')
    setDateToFilter('')
  }

  const hasActiveFilters = entityTypeFilter.length > 0 || actionFilter.length > 0 || !!dateFromFilter || !!dateToFilter

  const filteredEntries = useMemo(() => {
    setCurrentPage(1)
    const query = searchQuery.toLowerCase()
    return entries.filter((entry) =>
      !searchQuery ||
      entry.entityId.toLowerCase().includes(query) ||
      entry.actor.toLowerCase().includes(query) ||
      (typeof entry.changes === 'string' ? entry.changes : JSON.stringify(entry.changes))?.toLowerCase().includes(query)
    )
  }, [entries, searchQuery])

  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * CHANGELOG_PAGE_SIZE
    return filteredEntries.slice(start, start + CHANGELOG_PAGE_SIZE)
  }, [filteredEntries, currentPage])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-fn-text-muted">Loading changelog...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-critical-lighter rounded-lg border border-critical">
        <p className="text-critical font-medium">{error}</p>
        <Button
          onClick={() => fetchChangelog({
            ...(entityTypeFilter.length > 0 ? { entityType: entityTypeFilter.join(',') } : {}),
            ...(actionFilter.length > 0 ? { action: actionFilter.join(',') } : {}),
            ...(dateFromFilter ? { from: dateFromFilter } : {}),
            ...(dateToFilter ? { to: `${dateToFilter}T23:59:59Z` } : {}),
          })}
          variant="primary"
          className="mt-4"
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="funnels-theme space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-tf-text">Changelog</h1>
          <p className="text-tf-text-muted mt-1">Track all changes to funnels, offers, and steps</p>
        </div>
        <div className="text-sm text-tf-text-muted">
          Total: {filteredEntries.length} entr{filteredEntries.length !== 1 ? 'ies' : 'y'}
        </div>
      </div>
      <FilterSection
        searchQuery={searchQuery}
        entityTypeFilter={entityTypeFilter}
        actionFilter={actionFilter}
        dateFromFilter={dateFromFilter}
        dateToFilter={dateToFilter}
        hasActiveFilters={hasActiveFilters}
        onSearchChange={setSearchQuery}
        onEntityTypeChange={setEntityTypeFilter}
        onActionChange={setActionFilter}
        onDateFromChange={setDateFromFilter}
        onDateToChange={setDateToFilter}
        onClearFilters={clearFilters}
      />
      <Card>
        <CardSection className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fn-border bg-fn-muted">
                  <th className="text-left px-4 py-3 font-semibold text-fn-text-muted uppercase text-xs tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-fn-text-muted uppercase text-xs tracking-wide">Entity Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-fn-text-muted uppercase text-xs tracking-wide">Action</th>
                  <th className="text-left px-4 py-3 font-semibold text-fn-text-muted uppercase text-xs tracking-wide">Entity ID</th>
                  <th className="text-left px-4 py-3 font-semibold text-fn-text-muted uppercase text-xs tracking-wide">Actor</th>
                  <th className="text-left px-4 py-3 font-semibold text-fn-text-muted uppercase text-xs tracking-wide">Changes</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEntries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-fn-text-muted">
                      No changelog entries found.
                    </td>
                  </tr>
                ) : (
                  paginatedEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-fn-border hover:bg-fn-hover transition-colors animate-fade-slide-in">
                      <td className="px-4 py-3 text-fn-text-muted">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={getEntityTypeBadgeVariant(entry.entityType)}>
                          {entry.entityType}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={getActionBadgeVariant(entry.action)}>
                          {entry.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-fn-text-muted">
                        {entry.entityId}
                      </td>
                      <td className="px-4 py-3 text-fn-text-muted">
                        {entry.actor}
                      </td>
                      <td className="px-4 py-3 text-fn-text-muted">
                        {entry.changes ? (
                          <ul className="space-y-0.5">
                            {formatChanges(entry.changes).slice(0, 3).map((line, i) => (
                              <li key={i} className="line-clamp-1">{line}</li>
                            ))}
                            {formatChanges(entry.changes).length > 3 && (
                              <li className="text-fn-text-muted text-xs">+{formatChanges(entry.changes).length - 3} more</li>
                            )}
                          </ul>
                        ) : (
                          <span className="text-fn-text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-3">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredEntries.length}
              itemsPerPage={CHANGELOG_PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </div>
        </CardSection>
      </Card>
    </div>
  )
}
